import { FC, useEffect, useState } from 'react';
import { Link, Redirect } from 'react-router-dom';

import { DateTimeUtils } from '@servicetitan/datetime-utils';
import { BodyText, Card, Headline, Layout, Page, Stack } from '@servicetitan/design-system';
import { useDependencies } from '@servicetitan/react-ioc';

import { ApiModel, DOCUMENTATION_API_TOKEN } from '../../api/documentation.api';
import { PageSpinner } from '../../common';
import { Change, releases } from '../assets/releases';
import { ReleaseListSidebar } from './release-list-sidebar';
import * as Styles from './docs.module.scss';

const ChangeTable: FC<{ apiId: string; changes: Change[] }> = ({ apiId, changes }) => {
    const optionalBreak = '\u200B';

    return (
        <table className={Styles.simple}>
            <tbody>
                {changes.map((change, changeIndex) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <tr key={changeIndex}>
                        <td className="w-33">
                            <Stack direction="column" spacing={1}>
                                {change.endpoints.map(endpoint => (
                                    <Stack.Item key={endpoint.operationId}>
                                        <Link
                                            className="Link Link--blue"
                                            to={`/docs/apis/${apiId}/endpoints/${endpoint.operationId}`}
                                        >
                                            {`${endpoint.method} ${endpoint.shortPath.replaceAll(
                                                '/',
                                                '/' + optionalBreak,
                                            )}`}
                                        </Link>
                                    </Stack.Item>
                                ))}
                            </Stack>
                        </td>
                        <td>
                            <Stack direction="column" spacing={2}>
                                {change.description.split('\n').map((para, index) => (
                                    // eslint-disable-next-line react/no-array-index-key
                                    <Stack.Item key={index}>
                                        <BodyText>{para}</BodyText>
                                    </Stack.Item>
                                ))}
                                {change.fields && (
                                    <Stack.Item>
                                        <BodyText bold>{change.fieldsHeader}</BodyText>
                                        {change.fields.split('\n').map((para, index) => (
                                            // eslint-disable-next-line react/no-array-index-key
                                            <BodyText key={index}>{para}</BodyText>
                                        ))}
                                    </Stack.Item>
                                )}
                                {change.filters && (
                                    <Stack.Item>
                                        <BodyText bold>{change.filtersHeader}</BodyText>
                                        {change.filters.split('\n').map((para, index) => (
                                            // eslint-disable-next-line react/no-array-index-key
                                            <BodyText key={index}>{para}</BodyText>
                                        ))}
                                    </Stack.Item>
                                )}
                            </Stack>
                        </td>
                    </tr>
                ))}
            </tbody>
        </table>
    );
};

export const ReleaseDetailsPage: FC<{ version: string }> = ({ version }) => {
    const [dateTimeUtils, documentationApi] = useDependencies(
        DateTimeUtils,
        DOCUMENTATION_API_TOKEN,
    );
    const [apis, setApis] = useState<Map<string, ApiModel> | null>(null);

    useEffect(() => {
        documentationApi.getApis().then(apiGroups => {
            setApis(
                new Map(
                    apiGroups
                        .map(apiGroup => apiGroup.apis.map(api => [api.id, api] as const))
                        .flat(),
                ),
            );
        });
    }, [documentationApi]);

    const release = releases.find(r => r.version === version);
    if (!release) {
        return <Redirect to="/docs/release-notes" />;
    }

    return (
        <Page
            header={
                <Stack direction="column">
                    <Headline size="large">{`Release Notes for ST-${release.version}`}</Headline>
                    <BodyText subdued>
                        {dateTimeUtils.formatFullMonthDate(new Date(release.date + 'T00:00:00'))}
                    </BodyText>
                </Stack>
            }
            sidebar={<ReleaseListSidebar />}
        >
            <Layout>
                {apis === null ? (
                    <PageSpinner />
                ) : (
                    release.changeGroups.map(({ apiId, additions, updates }) => (
                        <Layout.Section key={apiId}>
                            <Card elevation="raised">
                                <Card.Section blue>
                                    <Headline className="m-0 c-inherit">
                                        {apis.get(apiId)?.displayName ?? apiId} API
                                    </Headline>
                                </Card.Section>
                                {additions && (
                                    <Card.Section>
                                        <Headline size="small">New Endpoints</Headline>
                                        <ChangeTable apiId={apiId} changes={additions} />
                                    </Card.Section>
                                )}
                                {updates && (
                                    <Card.Section>
                                        <Headline size="small">Updated Endpoints</Headline>
                                        <ChangeTable apiId={apiId} changes={updates} />
                                    </Card.Section>
                                )}
                            </Card>
                        </Layout.Section>
                    ))
                )}
            </Layout>
        </Page>
    );
};
ReleaseDetailsPage.displayName = 'ReleaseDetailsPage';
